<template>
  <div class="au-formgroup">
    <form
      class="au-card"
      :aria-label="STRINGS.aria.labels.loginForm"
      @submit.prevent="handleSubmit"
      autocomplete="on"
      novalidate
    >
      <!-- Email -->
      <div class="au-field">
        <label for="login-email" class="au-label">{{ STRINGS.auth.labels.email }}</label>
        <div class="au-input-wrap">
          <InputText
            id="login-email"
            ref="emailInput"
            v-model="email"
            type="email"
            name="email"
            class="au-input"
            :placeholder="STRINGS.auth.placeholders.email"
            autocomplete="username"
            :aria-invalid="!!localError"
            data-testid="login-email-input"
            @keyup.enter="handleSubmit"
          />
        </div>
      </div>

      <!-- Password -->
      <div class="au-field">
        <label for="login-password" class="au-label">{{ STRINGS.auth.labels.password }}</label>
        <div class="au-input-wrap">
          <InputText
            id="login-password"
            v-model="password"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            class="au-input au-input--pw"
            :placeholder="STRINGS.auth.placeholders.password"
            autocomplete="current-password"
            data-testid="login-password-input"
            @keyup.enter="handleSubmit"
          />
          <PasswordToggle :pressed="showPassword" @toggle="showPassword = !showPassword" />
        </div>
      </div>

      <!-- Error display -->
      <p
        v-if="displayError"
        ref="errorRef"
        class="au-hint au-hint--err text-center"
        role="alert"
        aria-live="polite"
        tabindex="-1"
      >
        {{ displayError }}
      </p>

      <!-- Submit button -->
      <button
        type="button"
        class="au-btn au-btn--primary au-mt"
        :disabled="!canSubmit"
        :aria-label="STRINGS.aria.labels.logIn"
        data-testid="login-submit-button"
        @click="handleSubmit"
      >
        <template v-if="auth.isLoading.value">
          {{ STRINGS.auth.buttons.loggingIn }}<LoadingDots />
        </template>
        <template v-else>
          {{ STRINGS.auth.buttons.logIn }}
        </template>
      </button>
    </form>

    <!-- Footer links -->
    <div class="au-flinks">
      <p class="au-flinks-row">
        {{ STRINGS.auth.links.noAccount }}
        <button
          class="au-link au-link--strong"
          @click="emit('navigate', 'signup')"
          data-testid="login-to-signup-link"
        >
          {{ STRINGS.auth.links.createAccount }}
        </button>
      </p>
      <button
        class="au-link au-link--strong"
        @click="emit('navigate', 'forgot-password')"
        data-testid="login-forgot-password-link"
      >
        {{ STRINGS.auth.links.forgotPassword }}
      </button>
      <button
        class="au-link au-link--muted"
        @click="emit('navigate', 'homepage')"
        data-testid="login-back-link"
      >
        {{ STRINGS.auth.links.backToHome }}
      </button>
      <template v-if="isMockAuth">
        <hr class="au-divider w-full" />
        <button
          class="au-link au-link--strong"
          data-testid="dev-login-button"
          @click="handleDevLogin"
        >
          Dev: Skip Login
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PasswordToggle from './ui/PasswordToggle.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { useAuth } from '@/composables/useAuth';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const emit = defineEmits<{
  success: [];
  navigate: [target: 'signup' | 'forgot-password' | 'homepage'];
}>();

const auth = useAuth();
const isMockAuth = AUTH_CONFIG.isMockAuthEnabled();

const email = ref('');
const password = ref('');
const showPassword = ref(false);
const localError = ref('');
const emailInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (auth.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(auth.rateLimitCountdown.value);
  }
  if (auth.error.value) {
    return auth.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  return (
    email.value.trim().length > 0 &&
    password.value.length > 0 &&
    !auth.isLoading.value &&
    auth.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  localError.value = '';
  auth.clearError();

  if (!email.value.trim()) {
    localError.value = STRINGS.auth.errors.emailRequired;
    focusError();
    return;
  }

  try {
    await auth.login({ email: email.value.trim(), password: password.value });
    emit('success');
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthLoginUnexpected);
    }
    focusError();
  }
}

async function handleDevLogin(): Promise<void> {
  if (auth.devLogin) {
    await auth.devLogin();
    emit('success');
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = emailInput.value?.$el ?? emailInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>
